import { Box, Typography, Container, useMediaQuery } from '@mui/material'
import { useState, useEffect } from 'react'
import { MenuItem } from 'models'
import { useCurrentStore, useOrdering, useEditOrderItem } from 'hooks'
import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import {
  checkCanShowEditItemDialog,
  addNonEditableItemToOrder,
  calculateUnavailableMessageWhenSearching,
  searchItemInMenu,
} from 'services'
import { MenuItemsList } from 'components/ordering/MenuItemsList'
import { MenuItemsGrid } from 'components/ordering/MenuItemsGrid'
import { SearchHeader } from './SearchHeader'
import { LoadingIndicator } from 'components/core/LoadingIndicator'
import { CheckoutFooter } from 'components/ordering/CheckoutFooter'

interface SearchAreaProps {
  open: boolean
  onClose: () => void
}

export const SearchArea = ({ open, onClose }: SearchAreaProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<MenuItem[]>([])
  const [isSearching, setIsSearching] = useState(false)

  const { t } = useTranslation()
  const { store } = useCurrentStore()
  const { order, saveOrder } = useOrdering()
  const { addOrderItem } = useEditOrderItem()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleSearch = (value: string) => {
    setSearchValue(value)
    setIsSearching(true)

    const results = searchItemInMenu(store, value)

    setSearchResults(results)
    setIsSearching(false)
  }

  const handleItemClick = (item: MenuItem) => {
    if (!order) return

    // Find the category that contains this item
    const category = store?.categories?.find((cat) =>
      cat.items?.some((i) => i.id === item.id),
    )

    if (!category) return

    if (checkCanShowEditItemDialog(item, store)) {
      addOrderItem(category, item)
    } else {
      saveOrder(addNonEditableItemToOrder(item, category, order))
    }
  }

  // Reset search when closing
  useEffect(() => {
    if (!open) {
      setSearchValue('')
      setSearchResults([])
    }
  }, [open])

  if (!open) return null

  // Get category for each search result
  const calculateUnavailableMessage = (item: MenuItem) => {
    return calculateUnavailableMessageWhenSearching(item, store, order)
  }

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: theme.palette.text.primary,
          opacity: 0.3,
          zIndex: 1200,
        }}
      />

      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1201,
        }}
      >
        <SearchHeader handleSearch={handleSearch} onClose={onClose} />

        <Box
          sx={{
            overflow: 'auto',
            bgcolor: searchValue ? theme.palette.primary.light : 'transparent',
            py: 2,
            flex: 1,
          }}
        >
          {searchValue && (
            <Container maxWidth="lg">
              {isSearching && <LoadingIndicator />}
              {!isSearching && searchResults.length > 0 ? (
                <>
                  <Typography
                    variant="body1"
                    sx={{
                      mb: 2,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t('We found {{count}} results for ', {
                      count: searchResults.length,
                    }) + `"${searchValue}"`}
                  </Typography>

                  {isMobile ? (
                    <MenuItemsList
                      items={searchResults}
                      onItemClick={handleItemClick}
                      sx={{ gap: 2 }}
                      calculateUnavailableMessageWhenSearching={
                        calculateUnavailableMessage
                      }
                    />
                  ) : (
                    <MenuItemsGrid
                      items={searchResults}
                      onItemClick={handleItemClick}
                      calculateUnavailableMessageWhenSearching={
                        calculateUnavailableMessage
                      }
                    />
                  )}
                </>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    color: theme.palette.text.primary,
                  }}
                >
                  {t('Sorry, we found 0 result for ') + `"${searchValue}"`}
                </Typography>
              )}
            </Container>
          )}
        </Box>

        <CheckoutFooter />
      </Box>
    </>
  )
}
